import posthog from "posthog-js";

import type { LocalUser } from "@/stores/useAuthStore";

let initialized = false;

// Public write-only keys (safe to ship in the bundle). The project is
// picked by hostname so no per-environment configuration is needed.
const POSTHOG_PROJECT_TOKENS = {
	dev: "phc_CRX2LXgS8TLL4wEDJ6BscBrVutebUsLi75hsvaekSWhG",
	prod: "phc_nhAf7GWkaX7yFBvNecDFZSbJQTaVZW3SrKpct2vcZsNo",
} as const;
const POSTHOG_HOST = "https://us.i.posthog.com";

function resolveProjectToken(): string {
	const hostname = window.location.hostname.toLowerCase();
	// Only the exact prod domain reports to prod. Everything else
	// (localhost, dev-value.marinachain.io, Amplify preview URLs) reports
	// to dev, so unknown hosts can never pollute prod numbers.
	if (hostname === "value.marinachain.io") {
		return POSTHOG_PROJECT_TOKENS.prod;
	}
	return POSTHOG_PROJECT_TOKENS.dev;
}

/**
 * Initializes PostHog once at app startup. Always enabled: the key is
 * public and write-only, so there is no per-environment configuration.
 */
export function initAnalytics(): void {
	if (initialized) return;
	posthog.init(resolveProjectToken(), {
		api_host: POSTHOG_HOST,
		defaults: "2026-05-30",
		// Explicit: dated `defaults` bundles can change SDK behavior over
		// time, and we always want baseline pageview/click capture.
		autocapture: true,
		capture_pageview: true,
		// Match the onboarding snippet: only identified (logged-in) users get
		// person profiles. Anonymous visitors are still counted in aggregates.
		person_profiles: "identified_only",
	});
	initialized = true;
}

export function isAnalyticsEnabled(): boolean {
	return initialized;
}

/** Links subsequent events to the logged-in user. Uses the stable backend user id. */
export function identifyUser(user: LocalUser): void {
	if (!initialized) return;
	posthog.identify(String(user.id), {
		email: user.email,
		name: user.name ?? undefined,
		company_id: user.company_id,
		company_name: user.company?.name ?? undefined,
	});
}

/** Clears the PostHog identity on logout so the next browser user starts fresh. */
export function resetAnalytics(): void {
	if (!initialized) return;
	posthog.reset();
}

/** v1 custom events (docs/product-events-v1.md). Keep this list small on purpose. */
export type TrackEventName =
	| "sign_up_completed"
	| "login_completed"
	| "onboarding_started"
	| "onboarding_completed"
	| "market_row_expanded"
	| "simulation_run"
	| "ship_searched"
	| "vessel_added"
	| "feedback_submitted";

export type TrackEventProperties = Record<
	string,
	string | number | boolean | null | undefined
>;

/**
 * Fires one custom event. Safe no-op before init (same pattern as
 * identifyUser). Never put PII in properties (US-cloud PostHog).
 */
export function trackEvent(
	name: TrackEventName,
	properties?: TrackEventProperties,
): void {
	if (!initialized) return;
	posthog.capture(name, properties);
}

/** Test-only escape hatch to reset module state between tests. */
export function __resetAnalyticsForTests(): void {
	initialized = false;
}
